import React, { useEffect, useState, useRef } from 'react';

import {
  useLocation, useParams, useSearchParams, useNavigate
} from 'react-router-dom';

import PaginationMC from '../presentation/components/PaginationMC';
import ButtonOpenFiltersMC from '../presentation/components/ButtonOpenFiltersMC';
import { getFiltersComponentByType } from '../util/paginationUtils';

/**

## Hook to manage pagination in a component.

  - Receives a modelService function with the app pagination structure example:
     usePagination(abstractModel(`${MERCATELY_API}/path-url`).get);

  - Receives a filters array with the filters to be displayed in the view.
    filters: [
      {
        label: 'Filter label',
        type: 'text' | 'search' | 'select' | 'date-range',
        key: 'filterName', // key to be used in the query params
        options?: [{ value: 'value', label: 'label' }],
        containerClassName?: class to be used in a column container,
      }
    ]
  - Receives a asideFilters array with the filters to be displayed in the aside view, same to filters.

  - Returns an object with the following properties:
    - data: array of objects with the data fetched.
    - loading: boolean that indicates if the data is being fetched.
    - paginationComponent: React component with the pagination buttons.

  - The hook handle all the pagination logic, you only need to call the modelService function with the query params. and use the paginationComponent in your view.

 */

const usePagination = ({
  modelService,
  filters,
  asideFilters,
  formatParams,
  persistedFilters = {}
}) => {
  const urlParams = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState([]);
  const [activeFullRoutePath, setActiveFullRoutePath] = useState(null);
  const [activeQueryParams, setActiveQueryParams] = useState(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isInitialLoad = useRef(true);

  const getPaginationComponent = () => (
    <PaginationMC
      onPageChange={({ selected }) => {
        const newSearchParams = Object.fromEntries([...searchParams]);
        newSearchParams.page = selected + 1;
        navigate({
          pathname: location.pathname,
          search: `?${new URLSearchParams(newSearchParams).toString()}`
        });
      }}
      totalPages={data.pages || data.total_pages || 0}
      currentPage={persistedFilters.page || activeQueryParams?.page || 1}
    />
  );

  const getFiltersComponent = () => (
    <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
      {(filters || []).map((filter, key) => getFiltersComponentByType(
        filter,
        key,
        activeQueryParams,
        navigate,
        searchParams
      ))}
      {asideFilters?.length && (
        <div className="tw-flex tw-flex-col tw-align-middle">
          <ButtonOpenFiltersMC asideFilters={asideFilters} activeQueryParams={activeQueryParams} />
        </div>
      )}
    </div>
  );

  const getData = async (params) => {
    const newParams = formatParams ? formatParams(params) : params;
    setLoading(true);
    modelService(newParams, urlParams).then((response) => {
      setData(response);
    }).catch(() => {
    }).finally(() => {
      setLoading(false);
    });
  };

  const refetch = (params) => {
    getData(params || activeQueryParams);
  };

  useEffect(() => {
    const currentSearch = location.search;
    if (activeFullRoutePath !== currentSearch) {
      setActiveFullRoutePath(currentSearch);
      const newSearchParams = Object.fromEntries([...new URLSearchParams(currentSearch || '')]);

      // Only merge persistedFilters on first load (e.g. return from product edit).
      // Re-merging on every URL change re-applied cleared sidebar filters from a stale ref.
      if (isInitialLoad.current) {
        Object.keys(persistedFilters || {}).forEach((key) => {
          if (newSearchParams[key] === undefined) {
            newSearchParams[key] = persistedFilters[key];
          }
        });
        isInitialLoad.current = false;
      }

      // Force page param to be always present
      if (newSearchParams.page === undefined) {
        newSearchParams.page = persistedFilters.page || 1;
      } else {
        newSearchParams.page = parseInt(newSearchParams.page, 10); // TODO: Check if this work for different pages
      }

      setActiveQueryParams(newSearchParams);
      getData(newSearchParams);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return {
    data,
    loading,
    paginationComponent: getPaginationComponent(),
    filtersComponent: getFiltersComponent(),
    refetch,
    activeQueryParams
  };
};

export default usePagination;
